import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsApi } from '../services/googleMapsService';
import { useTranslation } from 'react-i18next';
import config from '../config';

/**
 * Google Map View component.
 * Renders a Google Map with support for markers (with popups), circles, and center/zoom control.
 *
 * Props:
 *   center: { lat, lng }
 *   zoom: number (default 13)
 *   markers: Array<{ id, position: {lat,lng}, popup: React.ReactNode }>
 *   circle: { center: {lat,lng}, radius: number } | null
 *   markerIconUrl: string for custom marker icon URL
 *   markerIconSize: [number, number] for marker icon size
 *   className: string for additional classes
 *   style: object for additional styles
 *   height: string (default '450px')
 */
const GoogleMapView = ({
  center,
  zoom = 13,
  markers = [],
  circle = null,
  markerIconUrl,
  markerIconSize = [28, 28],
  className = '',
  style = {},
  height = '450px',
}) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const circleRef = useRef(null);
  const [apiStatus, setApiStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const infoWindowRef = useRef(null);

  // Load Google Maps API
  useEffect(() => {
    const apiKey = config.googleMaps?.apiKey;
    if (!apiKey) {
      setApiStatus('error');
      return;
    }

    let cancelled = false;

    loadGoogleMapsApi(apiKey)
      .then(() => {
        if (!cancelled) setApiStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setApiStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize map when API is ready
  useEffect(() => {
    if (apiStatus !== 'ready' || !mapRef.current || mapInstanceRef.current) return;
    if (!window.google?.maps) {
      setApiStatus('error');
      return;
    }

    try {
      const google = window.google;
      const map = new google.maps.Map(mapRef.current, {
        center: { lat: center?.lat || 10.7769, lng: center?.lng || 106.6952 },
        zoom: zoom,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
      });

      mapInstanceRef.current = map;
    } catch (err) {
      console.warn('[GoogleMapView] Map initialization failed:', err.message);
      setApiStatus('error');
    }
  }, [apiStatus, center, zoom]);

  // Update map center when center prop changes
  useEffect(() => {
    if (mapInstanceRef.current && center) {
      mapInstanceRef.current.setCenter({ lat: center.lat, lng: center.lng });
    }
  }, [center]);

  // Update zoom when zoom prop changes
  useEffect(() => {
    if (mapInstanceRef.current && zoom) {
      mapInstanceRef.current.setZoom(zoom);
    }
  }, [zoom]);

  // Update markers when markers prop changes
  useEffect(() => {
    if (!mapInstanceRef.current || apiStatus !== 'ready') return;

    const google = window.google;
    const map = mapInstanceRef.current;

    // Clear existing markers
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (markers.length === 0) return;

    markers.forEach((markerData) => {
      const marker = new google.maps.Marker({
        position: { lat: markerData.position.lat, lng: markerData.position.lng },
        map,
        title: markerData.title || '',
        animation: google.maps.Animation.DROP,
      });

      // Custom icon if URL provided
      if (markerIconUrl) {
        marker.setIcon({
          url: markerIconUrl,
          scaledSize: new google.maps.Size(markerIconSize[0], markerIconSize[1]),
        });
      }

      // Add click listener for popup
      if (markerData.popup) {
        marker.addListener('click', () => {
          // Close previous info window
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }

          const infoWindow = new google.maps.InfoWindow({
            content: markerData.popup,
            maxWidth: 300,
          });

          infoWindow.open(map, marker);
          infoWindowRef.current = infoWindow;
        });
      }

      markersRef.current.push(marker);
    });
  }, [markers, apiStatus, markerIconUrl, markerIconSize]);

  // Update circle when circle prop changes
  useEffect(() => {
    if (!mapInstanceRef.current || apiStatus !== 'ready') return;

    const google = window.google;
    const map = mapInstanceRef.current;

    // Remove existing circle
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    if (!circle) return;

    circleRef.current = new google.maps.Circle({
      strokeColor: '#8B5CF6',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#8B5CF6',
      fillOpacity: 0.08,
      map,
      center: { lat: circle.center.lat, lng: circle.center.lng },
      radius: circle.radius,
      editable: false,
      clickable: false,
      zIndex: 1,
    });
  }, [circle, apiStatus]);

  if (apiStatus === 'error') {
    return (
      <div
        className="flex items-center justify-center bg-gray-100 rounded-xl border border-gray-200"
        style={{ height, ...style }}
      >
        <div className="text-center p-6">
          <div className="text-4xl mb-3">🗺️</div>
          <p className="text-gray-500 text-sm font-medium">{t('map.googleMapsError')}</p>
          <p className="text-gray-400 text-xs mt-1">{t('map.googleMapsErrorHint')}</p>
        </div>
      </div>
    );
  }

  if (apiStatus === 'loading') {
    return (
      <div
        className="flex items-center justify-center bg-gray-100 rounded-xl border border-gray-200"
        style={{ height, ...style }}
      >
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      className={`rounded-xl overflow-hidden ${className}`}
      style={{ height, width: '100%', ...style }}
    />
  );
};

export default GoogleMapView;